import { cloneDeep } from 'lodash-es';
import jsYaml from 'js-yaml';
import applyPatch from './applyPatch';
import { resolveJitConfig, type JitConfigOverrides } from './audioJit';

export type CourseAudioJitProviderConfig = JitConfigOverrides & {
  cacheBaseUrl?: string;
};
export type CourseAudioJitLocaleConfig =
  | true
  | false
  | CourseAudioJitProviderConfig;
export type CourseAudioJitConfig =
  | boolean
  | CourseAudioJitProviderConfig
  | Record<string, CourseAudioJitLocaleConfig | undefined>;

const JIT_PATCH_PREFIX = '$course.audio-ai-jit';

/** Reserved key inside a per-locale audio-ai-jit dict: applies to any locale not explicitly listed. */
const JIT_DEFAULT_LOCALE_KEY = 'default';

const looksLikeProviderConfig = (
  config: CourseAudioJitConfig
): config is CourseAudioJitProviderConfig =>
  typeof config === 'object' &&
  !Array.isArray(config) &&
  config !== null &&
  ('provider' in config ||
    'model' in config ||
    'voice' in config ||
    'locale' in config ||
    'voiceLocale' in config ||
    'cacheBaseUrl' in config);

export const getPatchValue = (patchEntry: unknown): unknown => {
  if (Array.isArray(patchEntry) && patchEntry[0] != null) {
    const first = patchEntry[0] as { value?: unknown };
    if ('value' in first) return first.value;
  }
  return patchEntry;
};

export const getLocaleJitEntry = (
  config?: CourseAudioJitConfig,
  locale?: string
): CourseAudioJitLocaleConfig | null => {
  if (!config || !locale) return null;
  if (config === true) return true;

  if (looksLikeProviderConfig(config)) {
    return config;
  }

  if (typeof config === 'object' && !Array.isArray(config)) {
    const explicit = config[locale];
    if (explicit === false) return null; // explicit opt-out, even when `default` is on
    if (explicit != null) return explicit as CourseAudioJitLocaleConfig;

    const fallback = config[JIT_DEFAULT_LOCALE_KEY];
    if (fallback != null && fallback !== false)
      return fallback as CourseAudioJitLocaleConfig;

    return null;
  }

  return null;
};

export const buildJitOverrides = (
  localeConfig: CourseAudioJitLocaleConfig | null,
  courseLocale: string
): JitConfigOverrides => {
  if (!localeConfig || localeConfig === true) {
    return { locale: courseLocale, allowProviderFallback: true };
  }

  const { cacheBaseUrl: _cache, ...overrides } = localeConfig;
  return {
    ...overrides,
    locale: overrides.locale || courseLocale,
    allowProviderFallback: true
  };
};

/**
 * Rebuild `course['audio-ai-jit']` from YAML, applying `$course.audio-ai-jit…`
 * patches the same way {@link applyPatch} does at runtime (including nested paths
 * like `$course.audio-ai-jit.en-GB.voice`).
 */
export const extractAudioAiJitConfig = (
  parsedYaml: Record<string, unknown> | null | undefined
): CourseAudioJitConfig | undefined => {
  if (!parsedYaml) return undefined;

  const content = cloneDeep(parsedYaml) as unknown as Content;
  if (!content.course) {
    content.course = {} as Content['course'];
  }

  const patches = parsedYaml.patches as Record<string, unknown> | undefined;
  if (patches && typeof patches === 'object') {
    for (const [patchKey, patchEntry] of Object.entries(patches)) {
      if (!patchKey.startsWith(JIT_PATCH_PREFIX)) continue;
      if (!Array.isArray(patchEntry) || patchEntry.length === 0) continue;
      applyPatch(content)([patchKey, patchEntry as Patch]);
    }
  }

  return (content.course as Record<string, unknown> | undefined)?.[
    'audio-ai-jit'
  ] as CourseAudioJitConfig | undefined;
};

export const hasAudioAiJitInParsedYaml = (
  parsedYaml: Record<string, unknown> | null | undefined
): boolean => {
  const config = extractAudioAiJitConfig(parsedYaml);
  if (config === undefined || config === null) return false;
  if (config === true) return true;
  if (looksLikeProviderConfig(config)) return true;
  if (typeof config === 'object') {
    return Object.keys(config).length > 0;
  }
  return false;
};

export const isAudioJitEnabledForLocale = (
  config: CourseAudioJitConfig | undefined,
  locale: string
): boolean => {
  const localeConfig = getLocaleJitEntry(config, locale);
  if (!localeConfig) return false;

  try {
    resolveJitConfig(buildJitOverrides(localeConfig, locale));
    return true;
  } catch {
    return false;
  }
};

export const isAudioJitEnabledInYaml = (
  yamlString: string,
  locale: string
): boolean => {
  try {
    const parsed = jsYaml.load(yamlString) as Record<string, unknown>;
    return isAudioJitEnabledForLocale(extractAudioAiJitConfig(parsed), locale);
  } catch {
    return false;
  }
};

/** Locales with JiT enabled for the given config and candidate language codes. */
export const getJitEnabledLocalesFromConfig = (
  config: CourseAudioJitConfig | undefined,
  candidateLocales: string[]
): string[] => {
  if (!config) return [];

  if (config === true) {
    return [...candidateLocales];
  }

  const locales = new Set<string>(candidateLocales);

  if (
    typeof config === 'object' &&
    !Array.isArray(config) &&
    !looksLikeProviderConfig(config)
  ) {
    Object.keys(config)
      .filter(key => key !== JIT_DEFAULT_LOCALE_KEY)
      .forEach(locale => locales.add(locale));
  }

  return [...locales]
    .filter(locale => isAudioJitEnabledForLocale(config, locale))
    .sort();
};
